import React from "react";
import {FieldErrors, useFormContext} from "react-hook-form";
import * as Yup from "yup";

import {ContactsSelectField} from "@/components/form/fields/ContactsSelectField/ContactsSelectField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {ContactGroupBaseItems} from "@/lib/types/contacts.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
// Module Imports
import {CachetForm} from "@/pages/integrations/edit/subForms/CachetForm.tsx";
import {CachetShape} from "@/pages/integrations/edit/subForms/cachetInfo.ts";
import {DataDogForm} from "@/pages/integrations/edit/subForms/DataDogForm.tsx";
import {DataDogShape} from "@/pages/integrations/edit/subForms/dataDogInfo.ts";
import {DiscordForm} from "@/pages/integrations/edit/subForms/DiscordForm.tsx";
import {DiscordShape} from "@/pages/integrations/edit/subForms/discordInfo.ts";
import {GeckoBoardForm} from "@/pages/integrations/edit/subForms/GeckoBoardForm.tsx";
import {GeckoBoardShape} from "@/pages/integrations/edit/subForms/geckoBoardInfo.ts";
import {GoogleChatForm} from "@/pages/integrations/edit/subForms/GoogleChatForm.tsx";
import {GoogleChatShape} from "@/pages/integrations/edit/subForms/googleChatInfo.ts";
import {GrafanaGraphiteForm} from "@/pages/integrations/edit/subForms/GrafanaGraphiteForm.tsx";
import {GrafanaGraphiteShape} from "@/pages/integrations/edit/subForms/grafanaGraphiteInfo.ts";
import {GrafanaPrometheusForm} from "@/pages/integrations/edit/subForms/GrafanaPrometheusForm.tsx";
import {GrafanaPrometheusShape} from "@/pages/integrations/edit/subForms/grafanaPrometheusInfo.ts";
import {IlertForm} from "@/pages/integrations/edit/subForms/IlertForm.tsx";
import {IlertShape} from "@/pages/integrations/edit/subForms/ilertInfo.ts";
import {JiraForm} from "@/pages/integrations/edit/subForms/JiraForm.tsx";
import {JiraShape} from "@/pages/integrations/edit/subForms/jiraInfo.ts";
import {KlipfolioForm} from "@/pages/integrations/edit/subForms/KlipfolioForm.tsx";
import {KlipfolioShape} from "@/pages/integrations/edit/subForms/klipfolioInfo.ts";
import {LogzForm} from "@/pages/integrations/edit/subForms/LogzForm.tsx";
import {LogzShape} from "@/pages/integrations/edit/subForms/logzInfo.ts";
import {MakeForm} from "@/pages/integrations/edit/subForms/MakeForm.tsx";
import {MakeShape} from "@/pages/integrations/edit/subForms/makeInfo.ts";
import {MicrosoftTeamsForm} from "@/pages/integrations/edit/subForms/MicrosoftTeamsForm.tsx";
import {MicrosoftTeamsShape} from "@/pages/integrations/edit/subForms/microsoftTeamsInfo.ts";
import {OpsGenieForm} from "@/pages/integrations/edit/subForms/OpsGenieForm.tsx";
import {OpsGenieShape} from "@/pages/integrations/edit/subForms/opsGenieInfo.ts";
import {PagerDutyForm} from "@/pages/integrations/edit/subForms/PagerDutyForm.tsx";
import {PagerDutyShape} from "@/pages/integrations/edit/subForms/pagerDutyInfo.ts";
import {PushBulletForm} from "@/pages/integrations/edit/subForms/PushBulletForm.tsx";
import {PushBulletShape} from "@/pages/integrations/edit/subForms/pushBulletInfo.ts";
import {PushoverForm} from "@/pages/integrations/edit/subForms/PushoverForm.tsx";
import {PushoverShape} from "@/pages/integrations/edit/subForms/pushoverInfo.ts";
import {ServiceNowForm} from "@/pages/integrations/edit/subForms/ServiceNowForm.tsx";
import {ServiceNowShape} from "@/pages/integrations/edit/subForms/serviceNowInfo.ts";
import {SlackForm} from "@/pages/integrations/edit/subForms/SlackForm.tsx";
import {SlackShape} from "@/pages/integrations/edit/subForms/slackInfo.ts";
import {StatusForm} from "@/pages/integrations/edit/subForms/StatusForm.tsx";
import {StatusShape} from "@/pages/integrations/edit/subForms/statusInfo.ts";
import {StatusPageForm} from "@/pages/integrations/edit/subForms/StatusPageForm.tsx";
import {StatusPageShape} from "@/pages/integrations/edit/subForms/statusPageInfo.ts";
import {TelegramForm} from "@/pages/integrations/edit/subForms/TelegramForm.tsx";
import {TelegramShape} from "@/pages/integrations/edit/subForms/telegramInfo.ts";
import {VictorOpsForm} from "@/pages/integrations/edit/subForms/VictorOpsForm.tsx";
import {VictorOpsShape} from "@/pages/integrations/edit/subForms/victorOpsInfo.ts";
import {WaveFrontForm} from "@/pages/integrations/edit/subForms/WaveFrontForm.tsx";
import {WaveFrontShape} from "@/pages/integrations/edit/subForms/waveFrontInfo.ts";
import {WebhookForm} from "@/pages/integrations/edit/subForms/WebhookForm.tsx";
import {WebhookShape} from "@/pages/integrations/edit/subForms/webhookInfo.ts";
import {WhatsAppForm} from "@/pages/integrations/edit/subForms/WhatsAppForm.tsx";
import {WhatsAppShape} from "@/pages/integrations/edit/subForms/whatsAppInfo.ts";
import {ZapierForm} from "@/pages/integrations/edit/subForms/ZapierForm.tsx";
import {ZapierShape} from "@/pages/integrations/edit/subForms/zapierInfo.ts";
import {ZendeskForm} from "@/pages/integrations/edit/subForms/ZendeskForm.tsx";
import {ZendeskShape} from "@/pages/integrations/edit/subForms/zendeskInfo.ts";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

type integrationSchemaRecord = {
  shape: Yup.ObjectShape;
} | null;

const integrationsInfo: Record<IntegrationModules, integrationSchemaRecord> = {
  [IntegrationModules.CACHET]: {shape: CachetShape},
  [IntegrationModules.DATADOG]: {shape: DataDogShape},
  [IntegrationModules.DISCORD]: {shape: DiscordShape},
  [IntegrationModules.GECKOBOARD]: {shape: GeckoBoardShape},
  [IntegrationModules.GOOGLE_CHAT]: {shape: GoogleChatShape},
  [IntegrationModules.GRAFANA_GRAPHITE]: {shape: GrafanaGraphiteShape},
  [IntegrationModules.GRAFANA_PROMETHEUS]: {shape: GrafanaPrometheusShape},
  [IntegrationModules.ILERT]: {shape: IlertShape},
  [IntegrationModules.JIRA]: {shape: JiraShape},
  [IntegrationModules.KLIPFOLIO]: {shape: KlipfolioShape},
  [IntegrationModules.LOGZ]: {shape: LogzShape},
  [IntegrationModules.MAKE]: {shape: MakeShape},
  [IntegrationModules.MS_TEAMS]: {shape: MicrosoftTeamsShape},
  [IntegrationModules.OPSGENIE]: {shape: OpsGenieShape},
  [IntegrationModules.PAGERDUTY]: {shape: PagerDutyShape},
  [IntegrationModules.PUSHBULLET]: {shape: PushBulletShape},
  [IntegrationModules.PUSHOVER]: {shape: PushoverShape},
  [IntegrationModules.SERVICENOW]: {shape: ServiceNowShape},
  [IntegrationModules.SLACK]: {shape: SlackShape},
  [IntegrationModules.STATUS]: {shape: StatusShape},
  [IntegrationModules.STATUSPAGE]: {shape: StatusPageShape},
  [IntegrationModules.TELEGRAM]: {shape: TelegramShape},
  [IntegrationModules.SPLUNK]: {shape: VictorOpsShape},
  [IntegrationModules.WAVEFRONT]: {shape: WaveFrontShape},
  [IntegrationModules.WHATSAPP]: {shape: WhatsAppShape},
  [IntegrationModules.WEBHOOK]: {shape: WebhookShape},
  [IntegrationModules.ZAPIER]: {shape: ZapierShape},
  [IntegrationModules.ZENDESK]: {shape: ZendeskShape},
};

export const EditIntegrationInfoSchema = Yup.object().shape({
  id: Yup.number().optional(),
  name: Yup.string().required("This field cannot be blank."),
  module: Yup.string<IntegrationModules>().required(),
  is_enabled: Yup.boolean().optional(),
  contact_groups: Yup.array()
    .of(Yup.number().required())
    .required()
    .when("new_contact_groups", {
      is: (names?: string[]) => !names?.length,
      then: (schema) => schema.min(1, "You must assign at least one Contact."),
    }),
  new_contact_groups: Yup.array().of(Yup.string().required()).optional(),
  settings: Yup.object().when("module", (values, schema) => {
    const module = values[0];
    return module ? schema.shape(integrationsInfo[module as IntegrationModules]!.shape) : schema;
  }),
});

interface EditIntegrationFormProps {
  onSubmit: (data: IntegrationFormData, isDirty: boolean) => void;
  availableContactGroups: ContactGroupBaseItems;
  formId: string;
  disabled?: boolean;
  serverErrors?: ServerErrors;
  onError?: (errors: FieldErrors<IntegrationFormData>, event?: React.BaseSyntheticEvent) => void;
}

export const EditIntegrationForm = ({
  formId,
  onSubmit,
  onError,
  availableContactGroups,
  serverErrors,
}: EditIntegrationFormProps) => {
  const methods = useFormContext<IntegrationFormData>();

  const module = methods.watch("module");
  const {isDirty} = methods.formState;

  return (
    <form id={formId} onSubmit={methods.handleSubmit((data) => onSubmit(data, isDirty), onError)} noValidate>
      {serverErrors?.__all__ && <FormErrorAlerts errors={serverErrors?.__all__} />}

      <div className="row row-cols-2 mb-1">
        {(module && !IntegrationModuleInfo[module]?.readonly)! && (
          <div className={"mb-3 col-12"}>
            <InputField
              name="name"
              label="Name"
              required={true}
              tooltip={"Your preferred name for this integration."}
            />
          </div>
        )}
      </div>

      {!IntegrationModuleInfo[module]?.readonly && (
        <div className="row row-cols-2 mb-3">
          <div className="col-12 mb-3">
            <ContactsSelectField
              required={true}
              name={"contact_groups"}
              creatable
              label="Assign to Contacts"
              contacts={availableContactGroups.items}
              tooltip={"Assign your integration to a Contact to receive Alerts & Metrics from Checks…"}
            />
          </div>
        </div>
      )}

      {module === IntegrationModules.CACHET && <CachetForm serverErrors={serverErrors} />}
      {module === IntegrationModules.DATADOG && <DataDogForm serverErrors={serverErrors} />}
      {module === IntegrationModules.DISCORD && <DiscordForm serverErrors={serverErrors} />}
      {module === IntegrationModules.GECKOBOARD && <GeckoBoardForm serverErrors={serverErrors} />}
      {module === IntegrationModules.GOOGLE_CHAT && <GoogleChatForm serverErrors={serverErrors} />}
      {module === IntegrationModules.GRAFANA_GRAPHITE && <GrafanaGraphiteForm serverErrors={serverErrors} />}
      {module === IntegrationModules.GRAFANA_PROMETHEUS && <GrafanaPrometheusForm serverErrors={serverErrors} />}
      {module === IntegrationModules.ILERT && <IlertForm serverErrors={serverErrors} />}
      {module === IntegrationModules.JIRA && <JiraForm serverErrors={serverErrors} />}
      {module === IntegrationModules.KLIPFOLIO && <KlipfolioForm serverErrors={serverErrors} />}
      {module === IntegrationModules.LOGZ && <LogzForm serverErrors={serverErrors} />}
      {module === IntegrationModules.MAKE && <MakeForm serverErrors={serverErrors} />}
      {module === IntegrationModules.MS_TEAMS && <MicrosoftTeamsForm serverErrors={serverErrors} />}
      {module === IntegrationModules.OPSGENIE && <OpsGenieForm serverErrors={serverErrors} />}
      {module === IntegrationModules.PAGERDUTY && <PagerDutyForm serverErrors={serverErrors} />}
      {module === IntegrationModules.PUSHBULLET && <PushBulletForm serverErrors={serverErrors} />}
      {module === IntegrationModules.PUSHOVER && <PushoverForm serverErrors={serverErrors} />}
      {module === IntegrationModules.SERVICENOW && <ServiceNowForm serverErrors={serverErrors} />}
      {module === IntegrationModules.SLACK && <SlackForm serverErrors={serverErrors} />}
      {module === IntegrationModules.STATUS && <StatusForm serverErrors={serverErrors} />}
      {module === IntegrationModules.STATUSPAGE && <StatusPageForm serverErrors={serverErrors} />}
      {module === IntegrationModules.TELEGRAM && <TelegramForm serverErrors={serverErrors} />}
      {module === IntegrationModules.SPLUNK && <VictorOpsForm serverErrors={serverErrors} />}
      {module === IntegrationModules.WAVEFRONT && <WaveFrontForm serverErrors={serverErrors} />}
      {module === IntegrationModules.WHATSAPP && <WhatsAppForm serverErrors={serverErrors} />}
      {module === IntegrationModules.WEBHOOK && <WebhookForm serverErrors={serverErrors} />}
      {module === IntegrationModules.ZAPIER && <ZapierForm />}
      {module === IntegrationModules.ZENDESK && <ZendeskForm serverErrors={serverErrors} />}
    </form>
  );
};
